import { useListAgentExecutions } from "@/queries/agent-queries";
import { useListEnvironments, type PlainEnvironment } from "@/queries/environment-queries";
import { getRepoUrl } from "@/repo-url";
import { EnvironmentPhase, ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useMemo } from "react";

export type EnvironmentsGroup = {
    environments: PlainEnvironment[];
    repoFullName?: string;
    projectId?: string;
};

export type UseEnvironmentsByProjectsOptions = {
    includeAgentCreatedEnvironments?: boolean;
    archivalStatus?: ListEnvironmentsRequest_ArchivalStatus;
};

export function useEnvironmentsByProjects(options: UseEnvironmentsByProjectsOptions) {
    const { includeAgentCreatedEnvironments = false, archivalStatus = ListEnvironmentsRequest_ArchivalStatus.ALL } =
        options;

    const { data: environmentsData, isPending: isPendingEnvironments } = useListEnvironments({
        archivalStatus: archivalStatus,
    });

    const { data: agentExecutionsData, isPending: isPendingAgentExecutions } = useListAgentExecutions();

    const environmentsCreatedByAgents = useMemo(() => {
        const createdByAgents = new Set<string>();
        for (const execution of agentExecutionsData?.agentExecutions ?? []) {
            for (const envUsage of execution.status?.usedEnvironments ?? []) {
                if (envUsage.createdByAgent && envUsage.environmentId) {
                    createdByAgents.add(envUsage.environmentId);
                }
            }
        }
        return createdByAgents;
    }, [agentExecutionsData]);

    const data = useMemo(() => {
        const environments = environmentsData?.environments;
        if (environments === undefined) {
            return [];
        }

        const map = new Map<string, EnvironmentsGroup>();

        for (const environment of environments.filter(filterDeleted)) {
            const projectId = environment.metadata?.projectId;
            const repoUrl = getRepoUrl(environment);

            if (!includeAgentCreatedEnvironments && environmentsCreatedByAgents.has(environment.id)) {
                continue;
            }

            if (projectId) {
                const entry = map.get(projectId) || { projectId, environments: [] };
                entry.environments.push(environment);
                map.set(projectId, entry);
            } else if (repoUrl) {
                const repoFullName = `${repoUrl.account}/${repoUrl.repo}`;
                const entry = map.get(repoFullName) || { repoFullName, environments: [] };
                entry.environments.push(environment);
                map.set(repoFullName, entry);
            } else {
                const noRepository = "From scratch";
                const entry = map.get(noRepository) || { repoFullName: noRepository, environments: [] };
                entry.environments.push(environment);
                map.set(noRepository, entry);
            }
        }

        return Array.from(map.values()).sort(orderById);
    }, [environmentsCreatedByAgents, environmentsData?.environments, includeAgentCreatedEnvironments]);

    return {
        isLoading: isPendingEnvironments || isPendingAgentExecutions,
        data,
    };
}

function orderById(a: EnvironmentsGroup, b: EnvironmentsGroup): number {
    const toString = (g: EnvironmentsGroup) =>
        `${g.projectId || "x-no-project"}-${!g.projectId ? g.repoFullName : "is-project"}`;

    return toString(a).localeCompare(toString(b));
}

// Filter out environments that are being deleted or marked for deletion
// This ensures they disappear from the UI immediately
function filterDeleted(environment: PlainEnvironment): boolean {
    return (
        environment.spec?.desiredPhase !== EnvironmentPhase.DELETED &&
        environment.status?.phase !== EnvironmentPhase.DELETING
    );
}
